import {
    BottomSheet,
    Box,
    Button,
    type ButtonHierarchy,
    type ButtonState,
    type ButtonVariant,
    Divider,
    Modal,
    useMediaQueryContext,
} from "@drone-js/core";
import { HStack } from "apps/commons/components/Stack";
import { useDynamicLocation } from "apps/commons/hooks/useDynamicLocation";
import { LocationFormFields } from "apps/oneOnboarding/location/LocationFormFields";
import { useSetExpandManualLocationAtom } from "apps/oneOnboarding/location/expandManualLocationAtom";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import { oneOnboardingApi } from "libs/api";
import type { PropertyData } from "libs/codegen/property-onboarding";
import { type FunctionComponent, useEffect, useState } from "react";
import { type FieldPath, useFormContext } from "react-hook-form";
import { useParams } from "router";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";

interface Props {
    id: number;
    open: boolean;
    onClose: () => void;
    fieldPrefix: string;
}

type ValidPath = FieldPath<BulkPropertyFormData>;

export const LocationFormModal: FunctionComponent<Props> = ({ id, open, onClose, fieldPrefix }) => {
    const { trigger, getValues, setValue } = useFormContext<BulkPropertyFormData>();
    const context = useMediaQueryContext();
    const { lang } = useParams("/:lang/list-my-property/onboard/batch/location");
    const { pathname } = useDynamicLocation();
    const {
        cmsData: { bulkLocation, common },
    } = useStaticDataAtomValue();

    const [submitting, setSubmitting] = useState(false);
    const setExpand = useSetExpandManualLocationAtom();
    const [itemMemory, setItemMemory] = useState<PropertyData | undefined>(undefined);

    const handleClose = () => {
        if (submitting) return;

        // before closing, if itemMemory is not undefined (null is allowed), then let's restore it
        if (itemMemory !== undefined) {
            setValue(fieldPrefix as ValidPath, itemMemory);

            // reset itemMemory
            setItemMemory(undefined);
        }

        onClose();
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setExpand(true);

        await new Promise((resolve) => setTimeout(resolve, 10));

        const valid = await trigger(fieldPrefix as ValidPath);

        if (valid) {
            const propertyData = getValues(fieldPrefix as ValidPath) as PropertyData;
            oneOnboardingApi
                .savePropertyData(lang, id, pathname, undefined, propertyData)
                .then((result) => {
                    if (!result.data.success) throw new Error("Failed to save property data");

                    console.log(fieldPrefix);
                    setValue(fieldPrefix as ValidPath, propertyData);
                    onClose();
                })
                .catch(() => {})
                .finally(() => {
                    setSubmitting(false);
                });
        } else {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        // if state set to open, then let's store current state of filled location form
        // IMPORTANT: We must deep clone the values to avoid storing a reference that gets mutated
        if (open) {
            const currentValues = getValues(fieldPrefix as ValidPath) as PropertyData;
            setItemMemory(structuredClone(currentValues));
        }
    }, [open, fieldPrefix, getValues]);

    const confirmButtonProps = {
        variant: "label" as ButtonVariant,
        label: bulkLocation.confirm,
        hierarchy: "primary" as ButtonHierarchy,
        state: (submitting ? "disabled" : "enabled") as ButtonState,
        onClick: handleSubmit,
        "data-element-name": "confirm-location",
        "data-testId": "confirm-location",
    };

    const cancelButtonProps = {
        variant: "label" as ButtonVariant,
        label: bulkLocation.cancel,
        hierarchy: "secondary" as ButtonHierarchy,
        state: (submitting ? "disabled" : "enabled") as ButtonState,
        onClick: handleClose,
        "data-element-name": "cancel-location",
        "data-testId": "cancel-location",
    };

    if (!context.m)
        return (
            <BottomSheet
                title={bulkLocation.location}
                visible={open}
                onChange={handleClose}
                closeButton={{ "aria-label": "Close" }}
                data-element-name="location-modal"
                data-testId="location-modal"
            >
                <Box p="16">
                    <LocationFormFields fieldPrefix={fieldPrefix} />
                    <Box py="12" mx="-16">
                        <Divider />
                    </Box>
                    <HStack gap="8">
                        <Button {...cancelButtonProps} label={common.back} width="parent-fit" />
                        <Button {...confirmButtonProps} width="parent-fit" />
                    </HStack>
                </Box>
            </BottomSheet>
        );

    return (
        <Modal
            visible={open}
            onClose={handleClose}
            title={bulkLocation.location}
            size="m"
            actions={[
                {
                    actionType: "button",
                    ...confirmButtonProps,
                },
                {
                    actionType: "button",
                    ...cancelButtonProps,
                },
            ]}
            data-element-name="location-modal"
            data-testId="location-modal"
        >
            <LocationFormFields fieldPrefix={fieldPrefix} />
        </Modal>
    );
};
